import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Award,
  GraduationCap,
  Grid3X3,
  MapPin,
  Sparkles,
  Star,
} from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";

import { Button, ButtonRouter } from "~/components/ui/button";
import { CtaBanner } from "~/components/ui/cta-banner";
import { Heading } from "~/components/ui/heading";
import { NamedIcon } from "~/components/ui/named-icon";
import { cn } from "~/lib/cn";
import { getPublishedChallengesFn } from "~/lib/functions/cih/challenges";
import { getExpertiseCategories } from "~/lib/functions/expertise";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

type Category = Awaited<ReturnType<typeof getExpertiseCategories>>[number];

// -- Animation variants --

const STAGGER_CONTAINER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
} as const;

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
} as const;

// -- Browse dimension cards --

type BrowseDimension = {
  readonly to: string;
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly accent: string;
  readonly iconBg: string;
};

const BROWSE_DIMENSIONS: BrowseDimension[] = [
  {
    to: "/coaches/category",
    icon: <Grid3X3 className="h-6 w-6" />,
    title: "By category",
    description:
      "Life coaching, business, health, leadership, and more. Browse coaches by what they specialize in.",
    accent: "from-purple-500/10 to-purple-500/5",
    iconBg: "bg-purple-100 text-purple-700",
  },
  {
    to: "/coaches/city",
    icon: <MapPin className="h-6 w-6" />,
    title: "By city",
    description:
      "Find coaches near you or in any major city worldwide. Explore our interactive map.",
    accent: "from-blue-500/10 to-blue-500/5",
    iconBg: "bg-blue-100 text-blue-700",
  },
  {
    to: "/organizations",
    icon: <Award className="h-6 w-6" />,
    title: "By organization",
    description:
      "ICF, EMCC, and other leading bodies. Find coaches certified by the organizations you trust.",
    accent: "from-amber-500/10 to-amber-500/5",
    iconBg: "bg-amber-100 text-amber-700",
  },
  {
    to: "/training-programs",
    icon: <GraduationCap className="h-6 w-6" />,
    title: "By training program",
    description:
      "Browse coaches by where they trained. Training programs from around the world.",
    accent: "from-teal-500/10 to-teal-500/5",
    iconBg: "bg-teal-100 text-teal-700",
  },
  {
    to: "/coaches/founding",
    icon: <Star className="h-6 w-6" />,
    title: "Founding coaches",
    description:
      "Meet the pioneering coaches who helped shape Dream Coach Match from day one.",
    accent: "from-violet-500/10 to-violet-500/5",
    iconBg: "bg-violet-100 text-violet-700",
  },
];

const BrowseCard = ({ dim }: { readonly dim: BrowseDimension }) => (
  <motion.div variants={FADE_UP}>
    <Link
      to={dim.to}
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border border-gray-100 bg-gradient-to-br p-6",
        "shadow-sm transition duration-500 ease-out-expo",
        "hover:scale-[1.02] hover:border-gray-200 hover:shadow-lg",
        "focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:outline-none",
        dim.accent,
      )}
    >
      <div
        className={cn(
          "mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110",
          dim.iconBg,
        )}
      >
        {dim.icon}
      </div>
      <h3 className="text-text-primary font-heading text-lg font-medium tracking-tight">
        {dim.title}
      </h3>
      <p className="text-text-secondary mt-1.5 text-sm leading-relaxed">
        {dim.description}
      </p>
      <span className="mt-auto flex items-center gap-1.5 pt-4 text-sm font-medium text-brand-600 transition-colors group-hover:text-brand-700">
        Explore
        <span
          aria-hidden
          className="inline-block transition-transform duration-200 group-hover:translate-x-1"
        >
          &rarr;
        </span>
      </span>
    </Link>
  </motion.div>
);

// -- Category pill grid --

const CategoryPill = ({ category }: { readonly category: Category }) => (
  <motion.div variants={FADE_UP}>
    <Link
      to="/coaches/category/$categorySlug"
      params={{ categorySlug: category.slug }}
      className={cn(
        "group flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3",
        "shadow-sm transition duration-300 ease-out-expo",
        "hover:border-brand-200 hover:bg-brand-50/30 hover:shadow-md",
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 transition-colors group-hover:bg-brand-100">
        <NamedIcon
          name={category.icon || ""}
          className="h-5 w-5 text-brand-600"
        />
      </div>
      <span className="text-text-primary font-heading text-sm font-medium tracking-tight">
        {category.listingName || category.name}
      </span>
    </Link>
  </motion.div>
);

// -- "What are you navigating?" challenge cards --

type Challenge = Awaited<ReturnType<typeof getPublishedChallengesFn>>[number];
type LinkedChallenge = Challenge & { slug: string };

const ChallengeCard = ({
  challenge,
}: {
  readonly challenge: LinkedChallenge;
}) => (
  <motion.div variants={FADE_UP}>
    <Link
      to="/coaching-for/$challengeSlug"
      params={{ challengeSlug: challenge.slug }}
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6",
        "shadow-sm transition duration-500 ease-out-expo",
        "hover:scale-[1.02] hover:border-violet-200 hover:shadow-lg",
        "focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:outline-none",
      )}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50 text-violet-700 transition-transform duration-300 group-hover:scale-110">
        <NamedIcon name={challenge.icon || "sparkles"} className="h-6 w-6" />
      </div>
      <h3 className="text-text-primary font-heading text-lg font-medium tracking-tight">
        {challenge.title}
      </h3>
      {(challenge.tagline || challenge.description) && (
        <p className="text-text-secondary mt-1.5 line-clamp-3 text-sm leading-relaxed">
          {challenge.tagline ?? challenge.description}
        </p>
      )}
      <span className="mt-auto flex items-center gap-1.5 pt-4 text-sm font-medium text-violet-600 transition-colors group-hover:text-violet-700">
        Explore
        <span
          aria-hidden
          className="inline-block transition-transform duration-200 group-hover:translate-x-1"
        >
          &rarr;
        </span>
      </span>
    </Link>
  </motion.div>
);

const NavigatingSection = ({
  challenges,
}: {
  readonly challenges: LinkedChallenge[];
}) => {
  if (challenges.length === 0) return null;

  return (
    <section className="mx-auto max-w-screen-xl px-4 pb-16">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <Heading
          align="left"
          preHeadline="Start with your situation"
          headline="What are you navigating?"
          description="Coaching meets you where you are. Pick what's on your mind to explore frameworks, articles, and coaches who specialize in it."
        />
        <ButtonRouter
          to="/coaching-for"
          variant="ghost"
          className="text-violet-600"
        >
          See all guidance &rarr;
        </ButtonRouter>
      </div>

      <motion.div
        className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        variants={STAGGER_CONTAINER}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
      >
        {challenges.map((challenge) => (
          <ChallengeCard key={challenge.id} challenge={challenge} />
        ))}
      </motion.div>
    </section>
  );
};

// -- Hero --

const HeroSection = () => (
  <section className="relative overflow-hidden">
    {/* Decorative blurs */}
    <div
      className="pointer-events-none absolute top-0 left-1/2 -z-10 -translate-x-1/2"
      aria-hidden
    >
      <div className="h-[500px] w-[900px] rounded-full bg-purple-200/30 opacity-60 blur-[120px]" />
    </div>
    <div
      className="pointer-events-none absolute top-40 -right-32 -z-10"
      aria-hidden
    >
      <div className="h-[300px] w-[300px] rounded-full bg-blue-200/20 blur-[100px]" />
    </div>

    <div className="mx-auto max-w-screen-xl px-4 pt-16 pb-12 sm:pt-24 sm:pb-20">
      <div className="mx-auto max-w-3xl text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-2 text-sm font-medium tracking-wide text-purple-700 uppercase"
        >
          Dream Coach Match
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.1,
            type: "spring",
            stiffness: 280,
            damping: 22,
          }}
          className="text-text-primary font-heading text-4xl font-medium tracking-tight text-balance md:text-5xl lg:text-6xl"
        >
          Find the coach who{" "}
          <span className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 bg-clip-text text-transparent">
            gets you
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-text-secondary mx-auto mt-5 max-w-xl text-lg leading-relaxed"
        >
          Start with what you&rsquo;re navigating &mdash; career, relationships,
          health, a hard decision &mdash; and we&rsquo;ll connect you with
          coaches who specialize in it. Or browse the directory your way.
        </motion.p>
      </div>

      {/* AI Discovery CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.35,
          type: "spring",
          stiffness: 260,
          damping: 22,
        }}
        className="mx-auto mt-10 max-w-2xl"
      >
        <Link to="/discovery" className="group block">
          <div
            className={cn(
              "relative overflow-hidden rounded-2xl",
              "bg-gradient-to-br from-[#1a0a3e] via-[#2a1260] to-[#1a0a3e]",
              "p-8 sm:p-10",
              "shadow-xl shadow-purple-900/15 transition-shadow duration-500 ease-out-expo",
              "hover:shadow-2xl hover:shadow-purple-900/25",
              "border border-purple-500/10",
            )}
          >
            {/* Glow */}
            <div
              aria-hidden
              className="absolute top-1/2 left-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.18)_0%,transparent_70%)] transition-transform duration-700 group-hover:scale-110"
            />
            {/* Grid */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />

            <div className="relative z-10 flex flex-col items-center gap-5 text-center sm:flex-row sm:text-left">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-purple-500/20 backdrop-blur-sm transition-colors duration-300 group-hover:bg-purple-500/30">
                <Sparkles className="h-7 w-7 text-purple-300" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-medium tracking-tight text-white sm:text-2xl">
                  Find your coach with AI
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-purple-200/60 sm:text-base">
                  Answer a few questions about your goals and get personally
                  matched with coaches who specialize in exactly what you need.
                </p>
              </div>
              <Button
                size="lg"
                variant="fancy"
                className="shrink-0 shadow-[0_0_28px_rgba(168,85,247,0.3)]"
                tabIndex={-1}
              >
                Start discovery
              </Button>
            </div>
          </div>
        </Link>
      </motion.div>
    </div>
  </section>
);

// -- Page component --

function CoachesHubPage() {
  const { categories, challenges } = Route.useLoaderData();
  const navigatingChallenges = challenges
    .filter((c): c is LinkedChallenge => c.slug != null)
    .slice(0, 8);

  return (
    <>
      <HeroSection />

      <NavigatingSection challenges={navigatingChallenges} />

      {/* Browse dimensions — secondary, for people who'd rather filter by
          specialty or credential than start from their situation. */}
      <section className="mx-auto max-w-screen-xl px-4 pb-16">
        <Heading
          align="left"
          preHeadline="Or browse the directory"
          headline="Explore by category, city, or credential"
          description="Prefer to navigate by specialty or certification? Browse coaches every way."
        />

        <motion.div
          className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          variants={STAGGER_CONTAINER}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {BROWSE_DIMENSIONS.map((dim) => (
            <BrowseCard key={dim.to} dim={dim} />
          ))}
        </motion.div>
      </section>

      {/* Categories quick-access */}
      <section className="bg-gradient-to-b from-brand-50/40 to-white py-16">
        <div className="mx-auto max-w-screen-xl px-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <Heading
              align="left"
              preHeadline="Popular categories"
              headline="Jump into a category"
            />
            <Link to="/coaches/category">
              <Button variant="ghost" className="text-brand-600">
                View all categories &rarr;
              </Button>
            </Link>
          </div>

          <motion.div
            className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            variants={STAGGER_CONTAINER}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {categories.map((cat) => (
              <CategoryPill key={cat.id} category={cat} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <div className="mx-auto max-w-screen-xl px-4 py-16">
        <CtaBanner
          headline="Not sure where to start?"
          description="Our AI-powered discovery flow learns what matters to you and matches you with the right coach in minutes."
          buttonText="Start your journey"
          buttonHref="/discovery"
        />
      </div>
    </>
  );
}

// -- Route definition --

export const Route = createFileRoute("/_public/coaches/")({
  loader: async () => {
    const [categories, challenges] = await Promise.all([
      getExpertiseCategories(),
      getPublishedChallengesFn(),
    ]);
    return { categories, challenges };
  },
  head: () => {
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
    ]);
    return {
      ...createBrandMeta({
        title: "Find a Coach | Dream Coach Match",
        description:
          "Explore our directory of certified coaches by category, city, credentials, or training program. Or let our AI match you with the perfect coach for your goals.",
        ogTitle: "Find a Coach | Dream Coach Match",
        ogDescription:
          "Browse certified coaches by category, city, and credentials. Get AI-powered coach recommendations.",
        ogUrl: "https://dreamcoachmatch.com/coaches",
        canonical: "https://dreamcoachmatch.com/coaches",
        keywords: [
          "find a coach",
          "coaching directory",
          "life coach",
          "business coach",
          "certified coaches",
          "coach matching",
          "coaching categories",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  staticData: { title: "Find a Coach" },
  component: CoachesHubPage,
});
